import { IUserChatStatusService, UserChatStatus, MapUserChatStatus } from './user-chat-status-service-interface';
import { inject, injectable } from 'inversify';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { IConfigurationService, ConfigurationPaths, IChatConfigurationService, IChatModsConfiguration } from '@/app-modules/config';
import { IPersistedStore } from '@/app-modules/store/persisted-store';
import { UserPreferences } from '@/app-modules/store/modules/user-preferences';
import { HttpService, IHttpRequest } from '@/app-modules/http';
import { base64 } from '@/app-modules/utils';
import { User } from '@/app-modules/store/modules/user';
import { ChatStatusUserMap, ChatStatusUsers, EncodedChatStatusUsers } from '@/app-modules/store/modules/chat-status-user-map';

interface ITeamLeadSetting {
  teamLeadUsernames: string[];
  cmPrefix: string;
}

interface IShiftLeadSetting {
  shiftLeadUsernames: string[];
  cmPrefix: string;
}

interface ICustomerServiceSetting {
  customerServiceUsernames: string[];
}

@injectable()
class UserChatStatusService implements IUserChatStatusService {
  private readonly teamLeadSettings: { [key: string]: ITeamLeadSetting } = {};
  private readonly shiftLeadSettings: { [key: string]: IShiftLeadSetting } = {};
  private readonly customerServiceSettings: { [key: string]: ICustomerServiceSetting } = {};

  constructor(
    @inject(SERVICE_IDENTIFIERS.IConfigurationService) private readonly configurationService: IConfigurationService,
    @inject(SERVICE_IDENTIFIERS.IPersistedStore) private readonly persistedStore: IPersistedStore,
    @inject(SERVICE_IDENTIFIERS.IHttpService) private readonly httpService: HttpService
    ) {
  }

  public async fetchUserRolesFromServer(cmUser: User): Promise<ChatStatusUserMap> {

    const chatUserList = new ChatStatusUserMap();
    try {
      const encodedAuth = base64.Encode(`${cmUser.userId}:${cmUser.crosscheckId}`);
      const httpRequest: IHttpRequest = {
        Url: '/user/getmembers',
        XChatModsAuthorization: encodedAuth
      };
      const result = await this.httpService.get(httpRequest);
      if (result.status === 200 && result.data.Status === 200) {
        for (const [key, value] of new Map<string, string[]>(Object.entries(result.data.Data)).entries()) {
          chatUserList.set(MapUserChatStatus(key) as UserChatStatus, value);
        }
      }
    } catch (ex) {
      // TODO: log exception
    }

    return chatUserList;
  }

  public async getChatStatusForUser(username: string): Promise<UserChatStatus> {
    const teamLeadSettings = await this.getTeamLeadSettings();
    if (username.startsWith(`${teamLeadSettings.cmPrefix}-`)) {
      const shiftLeadSettings = await this.getShiftLeadSettings();

      if (teamLeadSettings.teamLeadUsernames.indexOf(username) > -1) {
        return UserChatStatus.TeamLeader;
      } else if (shiftLeadSettings.shiftLeadUsernames.indexOf(username) > -1) {
        return UserChatStatus.ShiftLeader;
      }
      return UserChatStatus.ChatModerator;
    }
    return UserChatStatus.StandardUser;
  }

  public async getCMPrefix(): Promise<string> {
    const teamLeadSettings = await this.getTeamLeadSettings();
    return teamLeadSettings.cmPrefix;
  }

  public async getUserCsStatus(username: string): Promise<boolean> {
    const country = this.getUserPreferredCountry().toLowerCase();
    const csMembers = await this.getCustomerServiceSettings(username);
    const teamLeadSettings = await this.getTeamLeadSettings();
    const cmPrefixUsername = teamLeadSettings.cmPrefix + '-' + username;
    // If user is a Team Lead, also allow them into the CS Room
    if (((csMembers.customerServiceUsernames.indexOf(username) > -1) || (teamLeadSettings.teamLeadUsernames.indexOf(cmPrefixUsername) > -1) || country === 'it') && country !== 'uk') {
      return true;
    }
    return false;
  }

  public async getInvalidWordsForPassword(): Promise<string[]> {
    const config = await this.configurationService.getConfiguration();
    return (config.getConfigurationByPath(ConfigurationPaths.words.invalidpassword) || '').replace(/\s/g, '').split(',');
  }

  private async getConfiguration(): Promise<IChatModsConfiguration> {
    const configuration = await this.configurationService.getConfiguration();
    return configuration;
  }

  private fetchUserRolesFromStore(): ChatStatusUsers {
    const encodedChatStatusUsers = this.persistedStore.Get(EncodedChatStatusUsers);
    return new ChatStatusUsers(encodedChatStatusUsers);
  }

  private async getTeamLeadSettings(): Promise<ITeamLeadSetting> {
    const country = this.getUserPreferredCountry();
    let teamLeadSettings = this.teamLeadSettings[country];
    if (!teamLeadSettings) {
      const cmPrefix = await this.getCMPrefixFromConfiguration();
      teamLeadSettings = {
        teamLeadUsernames: await this.getTeamLeadUsernamesFromConfiguration(cmPrefix),
        cmPrefix
      };
      this.teamLeadSettings[country] = teamLeadSettings;
    }
    return teamLeadSettings;
  }

  private async getShiftLeadSettings(): Promise<IShiftLeadSetting> {
    const country = this.getUserPreferredCountry();
    let shiftLeadSettings = this.shiftLeadSettings[country];
    if (!shiftLeadSettings) {
      const cmPrefix = await this.getCMPrefixFromConfiguration();
      shiftLeadSettings = {
        shiftLeadUsernames: await this.getShiftLeadUsernamesFromConfiguration(cmPrefix),
        cmPrefix
      };
      this.shiftLeadSettings[country] = shiftLeadSettings;
    }
    return shiftLeadSettings;
  }

  private async getCustomerServiceSettings(username: string): Promise<ICustomerServiceSetting> {
    const country = this.getUserPreferredCountry();
    let customerServiceSettings = this.customerServiceSettings[country];
    if (!customerServiceSettings) {
      customerServiceSettings = {
        customerServiceUsernames: await this.getCustomerServiceUsernamesFromConfiguration()
      };
      this.customerServiceSettings[country] = customerServiceSettings;
    }
    return customerServiceSettings;
  }

  private async getTeamLeadUsernamesFromConfiguration(cmPrefix: string): Promise<string[]> {
    const configuration = await this.getConfiguration();
    if (configuration.getConfigurationByPath(ConfigurationPaths.featuretoggle.use_active_directory_for_chat_user_roles_islive) === 'true') {
      const userMap = this.fetchUserRolesFromStore();
      return userMap.TeamLeaders.map((o) => `${cmPrefix}-${o.toLowerCase().trim()}`);
    } else {
      const config = await this.configurationService.getConfiguration();
      const teamLeads = config.getConfigurationByPath(ConfigurationPaths.users.teamleads) || '';
      return teamLeads.split(',').map((o) => `${cmPrefix}-${o.toLowerCase().trim()}`);
    }
  }

  private async getShiftLeadUsernamesFromConfiguration(cmPrefix: string): Promise<string[]> {
    const configuration = await this.getConfiguration();
    if (configuration.getConfigurationByPath(ConfigurationPaths.featuretoggle.use_active_directory_for_chat_user_roles_islive) === 'true') {
      const userMap = this.fetchUserRolesFromStore();
      return userMap.ShiftLeaders.map((o) => `${cmPrefix}-${o.toLowerCase().trim()}`);
    } else {
      const config = await this.configurationService.getConfiguration();
      const shiftLeads = config.getConfigurationByPath(ConfigurationPaths.users.shiftleads) || '';
      return shiftLeads.split(',').map((o) => `${cmPrefix}-${o.toLowerCase().trim()}`);
    }
  }

  private async getCustomerServiceUsernamesFromConfiguration(): Promise<string[]> {
    const config = await this.configurationService.getConfiguration();
    const csStaff = config.getConfigurationByPath(ConfigurationPaths.users.customerservices) || '';
    return csStaff.replace(/\s/g, '').split(',');
  }

  private async getCMPrefixFromConfiguration(): Promise<string> {
    const config = await this.configurationService.getConfiguration();
    return config.getConfigurationByPath(ConfigurationPaths.users.cmprefix) || 'CM';
  }

  private getUserPreferredCountry(): string {
    const userPreferences = this.persistedStore.Get(UserPreferences);
    return userPreferences.country;
  }
}

export { UserChatStatusService };



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/chat/_domain/modules/user-chat-status-service/user-chat-status-service.ts